// frontend/src/components/calendar/CalendarHolidaysSection.jsx
//
// Recurring annual holidays — one row per (month, day) at this
// calendar. Outlook-style: a list with an inline "+ Add" form and
// per-row remove. Name is optional.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  IconButton,
  TextField,
  MenuItem,
} from '@mui/material';
import { ICON, Plus, X } from '../../icons';

import {
  useCalendarHolidays,
  useCreateCalendarHoliday,
  useDeleteCalendarHoliday,
} from '../../hooks/useCalendarData';
import { formatMonthDayLabel } from './workingDayResolver';
import { translateRequestError } from '../../queries/api';
import '../../styles/calendar.css';
import { showToast } from '../../components/common/appToastBus';


function buildMonthOptions(locale) {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2001, i, 1).toLocaleDateString(locale, { month: 'long' }),
  }));
}

function daysInMonth(month, year = 2001) {
  // Use 2001 (non-leap) — 29 Feb isn't supported as a recurring annual
  // holiday; CalendarException covers leap-year edge cases.
  return new Date(year, month, 0).getDate();
}


const CalendarHolidaysSection = ({ calendar }) => {
  const { t, i18n } = useTranslation();
  const calendarId = calendar?.calendar_id;

  const { holidays } = useCalendarHolidays(calendarId);
  const createHoliday = useCreateCalendarHoliday(calendarId);
  const deleteHoliday = useDeleteCalendarHoliday();

  const [adding, setAdding] = useState(false);
  const [draftMonth, setDraftMonth] = useState(1);
  const [draftDay, setDraftDay] = useState(1);
  const [draftName, setDraftName] = useState('');

  const monthOptions = useMemo(() => buildMonthOptions(i18n.language), [i18n.language]);
  const dayOptions = useMemo(() => {
    const max = daysInMonth(draftMonth);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [draftMonth]);

  const startAdding = () => {
    setDraftMonth(1);
    setDraftDay(1);
    setDraftName('');
    setAdding(true);
  };

  const cancelAdding = () => setAdding(false);

  const saveHoliday = async () => {
    try {
      await createHoliday.mutateAsync({
        month: draftMonth, day: draftDay, holidayName: draftName.trim(),
      });
      setAdding(false);
    } catch (err) {
      console.error('createCalendarHoliday:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  const removeHoliday = async (id) => {
    try {
      await deleteHoliday.mutateAsync({ id });
    } catch (err) {
      console.error('deleteCalendarHoliday:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    }
  };

  const sortedHolidays = useMemo(
    () => [...holidays].sort((a, b) => (a.month - b.month) || (a.day - b.day)),
    [holidays],
  );

  return (
    <div className="calendar-overrides">
      <div className="calendar-overrides-header">
        <span className="calendar-overrides-count">
          {t('calendar.holidays.count', { count: holidays.length })}
        </span>
        {!adding && (
          <Button
            size="small"
            startIcon={<Plus size={ICON.md} />}
            onClick={startAdding}
          >
            {t('calendar.holidays.add')}
          </Button>
        )}
      </div>

      {sortedHolidays.length === 0 && !adding && (
        <div className="calendar-overrides-empty">
          {t('calendar.holidays.empty')}
        </div>
      )}

      <ul className="calendar-overrides-list">
        {sortedHolidays.map((h) => (
          <li key={h.calendar_holiday_id} className="calendar-overrides-row">
            <span className="calendar-overrides-date">
              {formatMonthDayLabel(h.month, h.day, i18n.language)}
            </span>
            <span className="calendar-overrides-name">
              {h.holiday_name || <span className="calendar-overrides-unnamed">—</span>}
            </span>
            <IconButton
              size="small"
              onClick={() => removeHoliday(h.calendar_holiday_id)}
              aria-label={t('btn.remove')}
            >
              <X size={ICON.sm} />
            </IconButton>
          </li>
        ))}
      </ul>

      {adding && (
        <div className="calendar-overrides-form">
          <TextField
            select
            size="small"
            value={draftMonth}
            onChange={(e) => setDraftMonth(Number(e.target.value))}
            label={t('calendar.holidays.month')}
            sx={{ minWidth: 140 }}
          >
            {monthOptions.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={draftDay}
            onChange={(e) => setDraftDay(Number(e.target.value))}
            label={t('calendar.holidays.day')}
            sx={{ minWidth: 80 }}
          >
            {dayOptions.map(d => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder={t('calendar.holidays.namePlaceholder')}
            label={t('calendar.holidays.name')}
            sx={{ flex: 1 }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={saveHoliday}
            loading={createHoliday.isPending}
          >
            {t('btn.add')}
          </Button>
          <Button size="small" onClick={cancelAdding}>
            {t('btn.cancel')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CalendarHolidaysSection;
